import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid,
} from "recharts";
import { Star } from "@phosphor-icons/react";
import api from "../lib/api";
import { initials, ROLE_LABELS } from "../lib/utils";

const Stars = ({ value = 0, size = 16 }) => (
  <div className="flex items-center gap-0.5">
    {[1, 2, 3, 4, 5].map((i) => (
      <Star key={i} size={size} weight={i <= Math.round(value) ? "fill" : "regular"}
        className={i <= Math.round(value) ? "text-amber-400" : "text-slate-300"} />
    ))}
  </div>
);

export default function Reports() {
  const [productivity, setProductivity] = useState([]);
  const [stats, setStats] = useState(null);
  const [ratings, setRatings] = useState(null);

  useEffect(() => {
    api.get("/reports/team-productivity").then((r) => setProductivity(r.data)).catch(() => setProductivity([]));
    api.get("/dashboard/stats").then((r) => setStats(r.data));
    api.get("/reports/ratings").then((r) => setRatings(r.data)).catch(() => {});
  }, []);

  const statusData = stats
    ? Object.entries(stats.by_status).map(([name, count]) => ({ name: name.replace(/_/g, " "), count }))
    : [];
  const prodData = productivity.map((p) => ({ name: p.user.name.split(" ")[0], hours: p.hours, resolved: p.resolved }));

  return (
    <div className="space-y-5" data-testid="reports-page">
      <div>
        <div className="text-[11px] uppercase tracking-[0.2em] text-blue-600 font-semibold">Analytics</div>
        <h1 className="font-display text-3xl font-bold tracking-tight text-slate-900 mt-1">Reports</h1>
        <p className="text-sm text-slate-500 mt-1">Productivity, workload distribution and ticket health.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="surface-card p-5" data-testid="status-distribution-chart">
          <div className="text-[10px] uppercase tracking-[0.15em] text-slate-400 font-semibold">Workload</div>
          <h3 className="font-display text-base font-semibold text-slate-900 mt-0.5 mb-4">Tickets by status</h3>
          <div className="h-[280px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={statusData} layout="vertical" margin={{ left: 60 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" horizontal={false} />
                <XAxis type="number" stroke="#94A3B8" fontSize={11} tickLine={false} axisLine={false} allowDecimals={false} />
                <YAxis dataKey="name" type="category" stroke="#94A3B8" fontSize={11} tickLine={false} axisLine={false} width={120} />
                <Tooltip contentStyle={{ borderRadius: 6, border: "1px solid #E2E8F0", fontSize: 12 }} />
                <Bar dataKey="count" fill="#2563EB" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="surface-card p-5" data-testid="productivity-chart">
          <div className="text-[10px] uppercase tracking-[0.15em] text-slate-400 font-semibold">Team</div>
          <h3 className="font-display text-base font-semibold text-slate-900 mt-0.5 mb-4">Hours logged per team member</h3>
          <div className="h-[280px]">
            {prodData.length === 0 ? (
              <div className="h-full flex items-center justify-center text-xs text-slate-400">No work logs yet.</div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={prodData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                  <XAxis dataKey="name" stroke="#94A3B8" fontSize={11} tickLine={false} axisLine={false} />
                  <YAxis stroke="#94A3B8" fontSize={11} tickLine={false} axisLine={false} />
                  <Tooltip contentStyle={{ borderRadius: 6, border: "1px solid #E2E8F0", fontSize: 12 }} />
                  <Bar dataKey="hours" fill="#10B981" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>
      </div>

      <div className="surface-card overflow-hidden" data-testid="productivity-table-card">
        <div className="px-5 py-4 border-b border-slate-200">
          <div className="text-[10px] uppercase tracking-[0.15em] text-slate-400 font-semibold">Team member productivity</div>
          <h3 className="font-display text-base font-semibold text-slate-900 mt-0.5">Hours, logs & resolved tickets</h3>
        </div>
        <div className="overflow-x-auto">
        <table className="w-full text-sm table-tight" data-testid="productivity-table">
          <thead className="bg-slate-50 text-left text-[10px] uppercase tracking-[0.1em] text-slate-500">
            <tr><th>Team Member</th><th>Role</th><th>Hours</th><th>Log entries</th><th>Resolved tickets</th></tr>
          </thead>
          <tbody>
            {productivity.length === 0 && <tr><td colSpan="5" className="text-center text-slate-400 py-10">No data yet.</td></tr>}
            {productivity.map((p) => (
              <tr key={p.user.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`productivity-row-${p.user.id}`}>
                <td>
                  <div className="flex items-center gap-2.5">
                    <div className="w-8 h-8 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center text-[11px] font-semibold">{initials(p.user.name)}</div>
                    <span className="font-medium text-slate-800">{p.user.name}</span>
                  </div>
                </td>
                <td className="text-slate-600">{ROLE_LABELS[p.user.role]}</td>
                <td className="font-mono text-slate-800">{p.hours}</td>
                <td className="text-slate-600">{p.logs}</td>
                <td className="text-slate-600">{p.resolved}</td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      </div>

      <div className="space-y-4" data-testid="satisfaction-section">
        <div>
          <div className="text-[10px] uppercase tracking-[0.15em] text-slate-400 font-semibold">Client feedback</div>
          <h3 className="font-display text-base font-semibold text-slate-900 mt-0.5">Customer satisfaction</h3>
        </div>
        {!ratings || ratings.summary.count === 0 ? (
          <div className="surface-card p-6 text-xs text-slate-400" data-testid="satisfaction-empty">
            No ratings yet — clients can rate tickets once they're completed.
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div className="surface-card p-6" data-testid="rating-avg-tile">
                <div className="text-[10px] uppercase tracking-[0.15em] text-slate-400 font-semibold">Average rating</div>
                <div className="mt-2 flex items-baseline gap-2">
                  <span className="font-display text-4xl font-bold text-slate-900 tabular-nums">{ratings.summary.avg}</span>
                  <span className="text-sm text-slate-500">/ 5</span>
                </div>
                <div className="mt-2"><Stars value={ratings.summary.avg} size={18} /></div>
              </div>
              <div className="surface-card p-6" data-testid="rating-count-tile">
                <div className="text-[10px] uppercase tracking-[0.15em] text-slate-400 font-semibold">Total ratings</div>
                <div className="mt-2 font-display text-4xl font-bold text-slate-900 tabular-nums">{ratings.summary.count}</div>
                <div className="text-xs text-slate-500 mt-1">of {ratings.summary.eligible} completed tickets</div>
              </div>
              <div className="surface-card p-6" data-testid="rating-response-tile">
                <div className="text-[10px] uppercase tracking-[0.15em] text-slate-400 font-semibold">Response rate</div>
                <div className="mt-2 flex items-baseline gap-1.5">
                  <span className="font-display text-4xl font-bold text-slate-900 tabular-nums">{ratings.summary.response_rate}</span>
                  <span className="text-sm text-slate-500">%</span>
                </div>
                <div className="w-full h-1.5 bg-slate-100 rounded-full mt-3 overflow-hidden">
                  <div className="h-full bg-amber-600" style={{ width: `${ratings.summary.response_rate}%` }} />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="surface-card p-5" data-testid="rating-distribution">
                <div className="text-[10px] uppercase tracking-[0.15em] text-slate-400 font-semibold mb-3">Distribution</div>
                <div className="space-y-2">
                  {[5, 4, 3, 2, 1].map((s) => {
                    const c = ratings.summary.distribution[String(s)] || 0;
                    const pct = ratings.summary.count ? Math.round((c / ratings.summary.count) * 100) : 0;
                    return (
                      <div key={s} className="flex items-center gap-2 text-xs">
                        <span className="w-7 text-slate-500 tabular-nums">{s} ★</span>
                        <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                          <div className="h-full bg-amber-600 rounded-full" style={{ width: `${pct}%` }} />
                        </div>
                        <span className="w-8 text-right text-slate-600 tabular-nums">{c}</span>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="surface-card p-5" data-testid="rating-recent">
                <div className="text-[10px] uppercase tracking-[0.15em] text-slate-400 font-semibold mb-3">Recent feedback</div>
                <div className="space-y-3 max-h-72 overflow-auto">
                  {ratings.recent.map((r) => (
                    <div key={r.id} className="border-b border-slate-100 pb-3 last:border-0 last:pb-0">
                      <div className="flex items-center justify-between gap-2">
                        <Stars value={r.rating} size={14} />
                        <span className="text-[11px] text-slate-400 whitespace-nowrap">{r.rated_at ? new Date(r.rated_at).toLocaleDateString() : ""}</span>
                      </div>
                      {r.rating_comment && <div className="text-xs text-slate-600 italic mt-1">"{r.rating_comment}"</div>}
                      <div className="text-[11px] text-slate-400 mt-1">
                        <Link to={`/tickets/${r.id}`} className="font-mono text-blue-600 hover:underline">{r.ticket_no}</Link>
                        {" · "}{r.organization_name || "—"}{r.project_name ? ` · ${r.project_name}` : ""}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
